import React, { useState, useEffect } from 'react';
import { VARIANT_TO_GROUP } from '../../stitches';
import { colorizeSvg } from '../../utils/svgColorize';

interface StitchIconProps {
  variantId: string;
  size?: number;
  color?: string;
}

// Cache: "url:color" → blob URL with replaced colors
const MAX_ICON_CACHE_SIZE = 500;
const colorizedCache: Record<string, string> = {};
const colorizedCacheKeys: string[] = []; // FIFO order
const pendingFetches: Record<string, Promise<string>> = {};

function getColorizedUrl(svgUrl: string, color: string): string | null {
  const key = `${svgUrl}:${color}`;
  if (colorizedCache[key]) return colorizedCache[key];

  if (!Object.prototype.hasOwnProperty.call(pendingFetches, key)) {
    pendingFetches[key] = fetch(svgUrl)
      .then(r => r.text())
      .then(text => {
        const colorized = colorizeSvg(text, color);
        const blob = new Blob([colorized], { type: 'image/svg+xml' });
        const blobUrl = URL.createObjectURL(blob);
        // Evict oldest entry if cache is full
        if (colorizedCacheKeys.length >= MAX_ICON_CACHE_SIZE) {
          const oldest = colorizedCacheKeys.shift()!;
          URL.revokeObjectURL(colorizedCache[oldest]);
          delete colorizedCache[oldest];
        }
        colorizedCache[key] = blobUrl;
        colorizedCacheKeys.push(key);
        delete pendingFetches[key];
        return blobUrl;
      })
      .catch(() => { delete pendingFetches[key]; return ''; });
  }

  return null;
}

export const StitchIcon: React.FC<StitchIconProps> = ({ variantId, size = 26, color }) => {
  const group = VARIANT_TO_GROUP[variantId];
  const iconUrl = group?.iconUrls[variantId];
  const [colorizedUrl, setColorizedUrl] = useState<string | null>(null);
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    if (!iconUrl || !color) { setColorizedUrl(null); return; }
    const cached = getColorizedUrl(iconUrl, color);
    if (cached) {
      setColorizedUrl(cached);
    } else {
      // Wait for fetch, then update
      const key = `${iconUrl}:${color}`;
      if (Object.prototype.hasOwnProperty.call(pendingFetches, key)) {
        pendingFetches[key].then(url => { setColorizedUrl(url); forceUpdate(n => n + 1); });
      }
    }
  }, [iconUrl, color]);

  if (!iconUrl) {
    return (
      <div style={{
        width: size, height: size, background: '#f0f0f0', borderRadius: 2,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 10, color: '#999'
      }}>?</div>
    );
  }

  const src = (color && colorizedUrl) ? colorizedUrl : iconUrl;

  return (
    <div style={{
      width: size, height: size,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <img
        src={src}
        alt={variantId}
        style={{
          display: 'block',
          maxWidth: size,
          maxHeight: size,
          width: 'auto',
          height: 'auto',
        }}
      />
    </div>
  );
};
